import React from 'react';
import { Button, StatusBadge } from '../common';
import { Typography, Text } from '../common/Typography';

/**
 * Visual progress stepper for tournament setup
 * Shows current stage and allows navigation to relevant sections
 */
const TournamentProgressStepper = ({ 
  steps = [], 
  currentStep = 1, 
  onStepClick, 
  interactive = true,
  orientation = 'horizontal' 
}) => {
  const isHorizontal = orientation === 'horizontal';

  const handleStepClick = (step) => {
    if (interactive && onStepClick && step.action) {
      onStepClick(step.action);
    }
  };

  return (
    <div className={`${isHorizontal ? 'flex items-start overflow-x-auto pb-4' : 'space-y-4'}`}>
      {steps.map((step, index) => {
        const isActive = step.number === currentStep;
        const isCompleted = step.completed;
        const isFuture = step.number > currentStep;
        const isDisabled = step.disabled;
        const isClickable = interactive && step.action && !isDisabled;

        return (
          <div
            key={step.number}
            className={`${isHorizontal ? 'flex items-start min-w-0' : 'flex items-start'}`}
          >
            {/* Step */}
            <div className={`flex ${isHorizontal ? 'flex-col items-center' : 'flex-row items-start'} ${isHorizontal ? 'min-w-[160px] sm:min-w-[200px]' : 'w-full'}`}>
              {/* Circle and connector */}
              <div className={`flex ${isHorizontal ? 'flex-col items-center' : 'flex-row items-center'} shrink-0`}>
                {/* Circle */}
                <button
                  type="button"
                  onClick={() => handleStepClick(step)}
                  disabled={!isClickable}
                  className={`
                    relative flex items-center justify-center rounded-full
                    transition-all duration-200
                    ${isHorizontal ? 'w-10 h-10 sm:w-12 sm:h-12' : 'w-10 h-10'}
                    ${isCompleted 
                      ? 'bg-success text-white shadow-md' 
                      : isActive 
                        ? 'bg-primary text-white shadow-md ring-4 ring-primary/20' 
                        : isDisabled
                          ? 'bg-surface-subtle text-text-muted border-2 border-border'
                          : 'bg-surface border-2 border-border-medium text-text-secondary'
                    }
                    ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
                    ${isActive && !isCompleted ? 'animate-pulse' : ''}
                  `}
                  aria-label={`${step.title}${isCompleted ? ' - Completed' : isActive ? ' - Current' : ''}`}
                >
                  {isCompleted ? (
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm sm:text-base font-bold">{step.number}</span>
                  )}
                </button>

                {/* Connector line */}
                {index < steps.length - 1 && (
                  <div className={`
                    ${isHorizontal 
                      ? 'w-full h-0.5 min-w-[40px] sm:min-w-[60px]' 
                      : 'h-12 w-0.5 ml-5 my-2'
                    }
                    ${isCompleted ? 'bg-success' : 'bg-border-medium'}
                    ${!isCompleted && !isHorizontal ? 'border-l-2 border-dashed border-border-medium bg-transparent' : ''}
                  `} />
                )}
              </div>

              {/* Step content */}
              <div className={`${isHorizontal ? 'mt-3 text-center' : 'ml-4 flex-1'} min-w-0`}>
                <Typography 
                  variant="strong" 
                  className={`
                    text-xs sm:text-sm mb-1 block
                    ${isActive ? 'text-primary' : isCompleted ? 'text-success' : 'text-text-secondary'}
                  `}
                >
                  {step.title}
                  {step.badge !== null && step.badge !== undefined && (
                    <StatusBadge 
                      variant={isActive ? 'primary' : isCompleted ? 'success' : 'default'} 
                      size="small" 
                      className="ml-2"
                    >
                      {step.badge}
                    </StatusBadge>
                  )}
                </Typography>
                <Text className="text-xs text-text-muted hidden sm:block">
                  {step.description}
                </Text>
                
                {/* Action button for current step */}
                {isActive && !isCompleted && isClickable && !isHorizontal && (
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => handleStepClick(step)}
                    className="mt-2"
                  >
                    Go to {step.title}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TournamentProgressStepper;
